import {useRef, useMemo} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import PropTypes from 'prop-types';

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  IconButton,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';

import {Markup} from '@amblique/base/app/components/shared/markup';
import {richTextToHtml} from '@amblique/contentful/app/components/rich-text-content';
import {useOverlayInitialFocus} from '@amblique/king-living/app/hooks/use-overlay-initial-focus';

/**
 * Reusable confirmation dialog for mode changes.
 * Shows a warning when user attempts to switch between customize/set modes with active selections.
 * Shared by ChooseYourSets and ConfigurationPanelModePicker for consistent UX.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the dialog is open
 * @param {Function} props.onCancel - Callback when user cancels (Stay and keep editing)
 * @param {Function} props.onConfirm - Callback when user confirms (Change set/mode)
 * @param {Object} [props.contentfulContent] - Optional Contentful rich text content for dialog body
 * @returns {React.ReactElement}
 */
export const ModeChangeConfirmationDialog = ({isOpen, onCancel, onConfirm, contentfulContent}) => {
  const intl = useIntl();
  const styles = useMultiStyleConfig('ConfigurationSelector', {});
  const closeButtonRef = useRef(null);
  const stayButtonRef = useRef(null);

  useOverlayInitialFocus({
    initialFocusRef: stayButtonRef,
    isOpen,
    preferHeading: false,
  });

  const contentHtml = useMemo(
    () => richTextToHtml(contentfulContent),
    [contentfulContent]
  );

  const confirmDialogTitle = intl.formatMessage({
    defaultMessage: 'Are you sure?',
    id: 'configuration_selector.panel_mode.confirm_title',
  });

  const stayButtonLabel = intl.formatMessage({
    defaultMessage: 'Stay and keep editing',
    id: 'configuration_selector.panel_mode.confirm_stay',
  });

  const changeSetButtonLabel = intl.formatMessage({
    defaultMessage: 'Change set',
    id: 'configuration_selector.panel_mode.confirm_change_set',
  });

  const confirmDialogCloseAriaLabel = intl.formatMessage({
    defaultMessage: 'Close dialog',
    id: 'configuration_selector.panel_mode.confirm_close',
  });

  return (
    <AlertDialog
      isCentered={false}
      isOpen={isOpen}
      leastDestructiveRef={stayButtonRef}
      onClose={onCancel}
    >
      <AlertDialogOverlay
        className="mode-change-confirm-overlay"
        {...styles.panelModePickerConfirmOverlay}
      >
        <AlertDialogContent
          className="mode-change-confirm-content"
          {...styles.panelModePickerConfirmContent}
        >
          <IconButton
            ref={closeButtonRef}
            aria-label={confirmDialogCloseAriaLabel}
            variant="unstyled"
            icon={
              <Box as="span" display="block" h="20px" w="20px">
                <svg
                  fill="none"
                  height="20"
                  viewBox="0 0 20 20"
                  width="20"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#mode-change-close-clip)" opacity={0.3}>
                    <path
                      d="M0.625 19.374L19.375 0.624023"
                      stroke="black"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M19.375 19.374L0.625 0.624023"
                      stroke="black"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="1.5"
                    />
                  </g>
                  <defs>
                    <clipPath id="mode-change-close-clip">
                      <rect fill="white" height="20" width="20" />
                    </clipPath>
                  </defs>
                </svg>
              </Box>
            }
            {...styles.panelModePickerConfirmCloseButton}
            h="20px"
            minW="20px"
            onClick={onCancel}
          />
          <AlertDialogHeader {...styles.panelModePickerConfirmHeader}>
            {confirmDialogTitle}
          </AlertDialogHeader>
          <AlertDialogBody {...styles.panelModePickerConfirmBody}>
            {contentHtml ? (
              <Markup source={contentHtml} />
            ) : (
              <FormattedMessage
                defaultMessage="You've made changes to this set.{br}If you switch to a different set, your current changes won't be saved."
                id="configuration_selector.panel_mode.confirm_body"
                values={{br: <br />}}
              />
            )}
          </AlertDialogBody>
          <AlertDialogFooter {...styles.panelModePickerConfirmFooter}>
            <Button
              ref={stayButtonRef}
              variant="unstyled"
              {...styles.panelModePickerConfirmButton}
              {...styles.panelModePickerConfirmButtonPrimary}
              onClick={onCancel}
            >
              {stayButtonLabel}
            </Button>
            <Button
              variant="unstyled"
              {...styles.panelModePickerConfirmButton}
              {...styles.panelModePickerConfirmButtonOutline}
              onClick={onConfirm}
            >
              {changeSetButtonLabel}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

ModeChangeConfirmationDialog.displayName = 'ModeChangeConfirmationDialog';

ModeChangeConfirmationDialog.propTypes = {
  contentfulContent: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
};
